import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Pencil, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminProducts, fetchCategories, deleteProduct, imgUrl } from "@/lib/api";
import { formatPrice } from "@/components/site/specIcons";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminProducts() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  const { data: products = [], isLoading } = useQuery({ queryKey: ["admin-products"], queryFn: adminProducts });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const catMap = Object.fromEntries(categories.map((c) => [c.slug, c.name]));

  const delMut = useMutation({
    mutationFn: (id) => deleteProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success("Ürün silindi");
      setToDelete(null);
    },
    onError: () => toast.error("Silme başarısız"),
  });

  const filtered = products.filter((p) => {
    const okCat = cat === "all" || p.category === cat;
    const okSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || "").toLowerCase().includes(search.toLowerCase());
    return okCat && okSearch;
  });

  return (
    <div>
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold">Ürünler</h1>
          <p className="text-sm text-zinc-500">{products.length} ürün · kolayca ekleyin, düzenleyin veya silin</p>
        </div>
        <button
          data-testid="add-product-btn"
          onClick={() => navigate("/admin/products/new")}
          className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Yeni Ürün Ekle
        </button>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="product-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ürün adı veya kod ara..."
            className="w-full rounded-lg border border-zinc-300 py-2.5 ps-9 pe-3 text-sm outline-none focus:border-primary"
          />
        </div>
        <select
          data-testid="product-cat-filter"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="rounded-lg border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-primary"
        >
          <option value="all">Tüm Kategoriler</option>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 py-20 text-center text-zinc-500">
          Ürün bulunamadı. <button onClick={() => navigate("/admin/products/new")} className="font-semibold text-primary">Yeni ürün ekleyin</button>.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3">Ürün</th>
                <th className="hidden px-4 py-3 md:table-cell">Kategori</th>
                <th className="hidden px-4 py-3 sm:table-cell">Fiyat</th>
                <th className="px-4 py-3">Durum</th>
                <th className="px-4 py-3 text-end">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`admin-product-${p.id}`} className="hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-10 overflow-hidden rounded bg-zinc-100">
                        {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 font-medium text-zinc-900">
                          {p.name}
                          {p.featured && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
                        </div>
                        <div className="text-xs text-zinc-400">{p.sku || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-zinc-600 md:table-cell">{catMap[p.category] || p.category}</td>
                  <td className="hidden px-4 py-3 font-medium sm:table-cell">{formatPrice(p.price)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.active ? "bg-green-100 text-green-700" : "bg-zinc-200 text-zinc-500"}`}>
                      {p.active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`edit-${p.id}`} onClick={() => navigate(`/admin/products/${p.id}`)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-100">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button data-testid={`delete-${p.id}`} onClick={() => setToDelete(p)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ürünü sil?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.name}" kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={() => delMut.mutate(toDelete.id)} className="bg-red-600 hover:bg-red-700">
              {delMut.isPending ? "Siliniyor..." : "Evet, Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
